"use client";

import { useState, useMemo,useEffect } from "react";
import { subDays, isWithinInterval, startOfDay, endOfDay, set } from "date-fns";
import { Moon, Sun, BarChart3 } from "lucide-react";
import { useThemeStore } from "context/useThemeStore";
import { format, subMonths } from "date-fns";
import AnalyticsFilters from "./analytics-filters";
import KpiCards from "./kpi-cards";
import UsageOverTimeChart from "./usage-over-time-chart";
import TopPromptsChart from "./top-prompts-chart";
import PromptHealthChart from "./prompt-health-chart";
import RoleDistributionChart from "./role-distribution-chart";
import UserLeaderboard from "./user-leaderboard";
import PolicyRiskChart from "./policy-risk-chart";
import localization from "services/localization";
import { useLanguageStore } from "context/useLanguageStore";
import SpecificAPI from "services/SpecificApi";
import { useDepartmentsStore } from "context/useDepartmentsStore";
import { useUserStore } from "context/useUserStore";
import { usePromptsStore } from "context/usePromptsStore";
import { toast } from "react-toastify";
import { ClipLoader } from "react-spinners";
import ManagementHeader from "views/Components/ManagementHeader";

const today = new Date();
const oneMonthAgo = subMonths(today, 1);

const defaultRange = {
  from: oneMonthAgo,
  to: today,
};

const DEFAULT_FILTERS = {
  role: "all",
  user: "all",
  department: "all",
  timePreset: "custom",
  dateRange: defaultRange,
};

export default function AnalyticsDashboard() {
  const { currentLang } = useLanguageStore();
  const { theme } = useThemeStore();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [stasticsData , setStasticsData] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const { appUser, userData} = useUserStore();
  const { prompts } = usePromptsStore();
  const { departments } = useDepartmentsStore();


    useEffect(() => {
      getAllData();
      const filteredDepartments = departments.filter(
            (d) => d.school_id === userData?.selected_school_id
          );
          setFilteredDepartments(filteredDepartments);
    }, []);

    const getAllData = async () => {
        setLoading(true);
        try {
          let schoolId = userData?.selected_school_id
          const response = await SpecificAPI.getAllUsers();
          const users = (response || []).filter(u =>
            u.school_id?.includes(userData?.selected_school_id)
          );
          setUsers(users);
          
          const availableRoles = [
  "all",
  ...Array.from(new Set(users.map((u) => u.role).filter((r) => r !== "root")))
];
setAvailableRoles(availableRoles);

         const res = await SpecificAPI.getStastics(schoolId, {
                  per_page: 50,
                  date_start: format(filters.dateRange.from, "yyyy-MM-dd"),
                  date_end: format(filters.dateRange.to, "yyyy-MM-dd"),
                });
          const stastics = res.data;
          setStasticsData(stastics);
        } catch (err) {
          toast.error("Failed to fetch staff members.");
          console.error("Error fetching staff members:", err);
        } finally {
          setLoading(false);
        }
      };

  const filteredMessages = useMemo(() => {
    // let msgs = [...sampleMessages];
let msgs = stasticsData;

if(stasticsData.length > 0){
   if (filters.user !== "all") {
      msgs = msgs.filter((m) => m.user_id === filters.user);
    }

    if (filters.department !== "all") {
      msgs = msgs.filter((m) => m.department_id === filters.department);
      debugger
    }

    if (filters.role !== "all") {
    const userIdsWithRole = users
      .filter((u) => u.role === filters.role)
      .map((u) => u.id);
    msgs = msgs.filter((m) => userIdsWithRole.includes(m.user_id));
  }

   const now = new Date();
if (filters.timePreset === "custom" && filters.dateRange?.from) {
  const from = startOfDay(filters.dateRange.from);
  const to = filters.dateRange.to
    ? endOfDay(filters.dateRange.to)
    : endOfDay(filters.dateRange.from);
  msgs = msgs.filter((m) =>
    isWithinInterval(new Date(m.date), { start: from, end: to })
  );
} else {
  const days = parseInt(filters.timePreset) || 30;
  const from = subDays(now, days);
  msgs = msgs.filter((m) => new Date(m.date) >= from);
}
}
   

    return msgs;
  }, [filters,stasticsData]);

  // Filter users displayed in select based on role / department selection
const availableUsers = useMemo(() => {
  return users.filter(u => 
    (filters.role === "all" || u.role === filters.role)
  );
}, [users, filters.role]);

const allowedDepartmentTypesByRole = {
  student: ["classroom", "utilities"],
  teacher: ["classroom", "utilities"],
  administrative: ["hr", "administrative", "utilities"],
};

const handleFilterChange = (newFilters) => {
  let updatedFilters = { ...filters, ...newFilters };
  if (
    newFilters.department &&               
    newFilters.department !== filters.department && 
    filters.user !== "all"                
  ) {
    updatedFilters.user = "all";
  }

  if (newFilters.role !== "all" && updatedFilters.user !== "all") {
    const userStillValid = users.find(
      (u) => u.id === updatedFilters.user && u.role === updatedFilters.role
    );
    if (!userStillValid) updatedFilters.user = "all";
  }
  if (newFilters.user !== "all" && updatedFilters.department !== "all") {
    updatedFilters.department = "all";
  }

  setFilters(updatedFilters);
};

const availableDepartments = useMemo(() => {
  if (filters.role === "all") return filteredDepartments;

  const allowedTypes = allowedDepartmentTypesByRole[filters.role.toLowerCase()] || [];

  return filteredDepartments.filter(d => allowedTypes.includes(d.type));
}, [filteredDepartments, filters.role]);

const computedDateRange = useMemo(() => {
  const now = new Date();

  if (filters.timePreset === "custom" && filters.dateRange?.from) {
    return {
      from: filters.dateRange.from,
      to: filters.dateRange.to || filters.dateRange.from,
    };
  }

  // preset is number of days
  const days = parseInt(filters.timePreset) || 30;
  return {
    from: subDays(now, days),
    to: now,
  };
}, [filters.timePreset, filters.dateRange]);

  const page_bg =
    theme === "dark" ? "bg-[#181a1b] text-foreground" : "bg-background text-foreground";

  return (
    <div className={`min-h-screen ${page_bg} d-flex flex-column p-8 mb-5 w-100 main-page`}>
        <ManagementHeader
          title={localization.anlytics || "Analytics"}
          subtitle={localization.edu_usage_insights || "EDU usage insights"}
          showBack={true}
          showAdd={false}
          backTo="/schools"
          backLabel="Back to organization"
        />

      {/* Main Content */}
      <main className="mx-auto w-full">
         {loading ? (
          <div className="flex justify-center items-center h-96">
            <ClipLoader size={50} color="var(--primary-icon)"/>
          </div>
        ) : (
        <div className="flex flex-col gap-6">
          {/* Filters */}
          <div
            className={`rounded-lg border p-4 ${
              theme === "dark"
                ? "bg-[var(--card-bg)] border-border"
                : "bg-card border-border"
            }`}
          >
            <AnalyticsFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              users={availableUsers}
              departments={availableDepartments}
              availableRoles={availableRoles}
              theme={theme}
            />
          </div>

          {/* KPI Cards */}
          <KpiCards
            messages={filteredMessages}
            users={availableUsers}
            prompts={prompts}
            theme={theme}
          />

          {/* Usage Over Time Chart */}
          <UsageOverTimeChart messages={filteredMessages} theme={theme} users={availableUsers} dateRange={computedDateRange} selectedUser={filters.user}/>

          {/* Charts Row: Top Prompts + Role Distribution */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <TopPromptsChart
              messages={filteredMessages}
              prompts={prompts}
              theme={theme}
            />
            <RoleDistributionChart
              messages={filteredMessages}
              theme={theme}
              users={availableUsers}
              departments={filteredDepartments}
              selectedUser={filters.user}
              selectedRole={filters.role}
            />
          </div>

          {/* Charts Row: Prompt Health + Policy Risk */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* <PromptHealthChart messages={filteredMessages} theme={theme} /> */}
            <PolicyRiskChart messages={filteredMessages} theme={theme} departments={filteredDepartments} users={availableUsers} selectedRole={filters.role} selectedUser={filters.user} selectedDepartment={filters.department}/>
          </div>

          {/* User Leaderboard */}
          <UserLeaderboard messages={filteredMessages} theme={theme} users={availableUsers} departments={filteredDepartments} selectedDepartment={filters.department}/>
        </div>
        )}
      </main>
    </div>
  );
}
